import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { CheckCircle2, Package, Truck, MapPin, MessageCircle, QrCode, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { formatINR, waLink, buildOrderMessage, assetUrl } from "@/config";
import { useSettings } from "@/context/SettingsContext";
import { OrderTimeline } from "@/components/OrderTimeline";

export default function OrderConfirmation() {
  const { orderId } = useParams();
  const { settings } = useSettings();
  const [order, setOrder] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/orders/${orderId}`).then((r) => setOrder(r.data)).catch(() => setNotFound(true));
  }, [orderId]);

  if (notFound) return <div className="mx-auto max-w-3xl px-4 py-20 text-center text-[#6B5B52]">Order not found.</div>;
  if (!order) return <div className="mx-auto max-w-3xl px-4 py-20 text-center text-[#6B5B52]">Loading order...</div>;

  const isPaid = order.payment_status === "paid";
  const isQR = order.payment_method === "qr" && !isPaid;

  const waMsg = buildOrderMessage({
    customer: order.customer,
    items: order.items.map((i) => ({ name: i.name, weight_label: i.weight_label, qty: i.qty, price: i.unit_price })),
    subtotal: order.subtotal, discount: order.discount, shipping: order.shipping, total: order.total,
  });

  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 py-10">
      <div className="text-center">
        <div className="mx-auto h-16 w-16 rounded-full bg-[#EAF6EC] grid place-items-center">
          <CheckCircle2 size={38} className="text-[#15803D]" />
        </div>
        <h1 className="font-display text-3xl font-bold text-[#1F130B] mt-4" data-testid="order-confirmation-title">
          {isPaid ? "Order Confirmed!" : "Order Placed!"}
        </h1>
        <p className="text-[#6B5B52] mt-2">Thank you, {order.customer.name}! {isPaid ? "Your payment was successful." : "We'll confirm your order shortly."}</p>
        <p className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#FEF3C7] text-[#9A3412] font-semibold px-4 py-1.5 text-sm" data-testid="order-id">
          Order ID: {order.order_id}
        </p>
      </div>

      {isQR && (
        <div className="mt-8 rounded-2xl bg-white border-2 border-[#D97706] p-6 text-center" data-testid="qr-payment-box">
          <p className="inline-flex items-center gap-2 text-[#9A3412] font-semibold"><QrCode size={18} /> Scan & Pay via UPI</p>
          <div className="mt-4 inline-block rounded-2xl border border-[#E7E0D6] p-3 bg-white">
            <img src={assetUrl(settings.qr_image)} alt="UPI Payment QR" className="mx-auto w-60 h-auto rounded-lg" data-testid="upi-qr-image" />
          </div>
          <p className="mt-4 text-sm text-[#6B5B52]">Pay <b className="text-[#1F130B]">{formatINR(order.total)}</b> using any UPI / BHIM app to</p>
          <p className="font-semibold text-[#1F130B]">{settings.upi_name}</p>
          <p className="text-sm text-[#6B5B52]">{settings.upi_id}</p>
          <p className="mt-3 text-xs text-[#9CA3AF]">After payment, send the screenshot on WhatsApp with your Order ID to confirm.</p>
          <a href={waLink(`Hi! I've paid ${formatINR(order.total)} for order ${order.order_id}. Attaching payment screenshot.`)} target="_blank" rel="noreferrer" className="inline-flex mt-4 items-center gap-2 rounded-full bg-[#25D366] text-white font-semibold px-6 py-2.5">
            <MessageCircle size={17} /> Send Payment Proof
          </a>
        </div>
      )}

      <div className="mt-8 rounded-2xl bg-white border border-[#E7E0D6] p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-lg font-bold text-[#1F130B]">Order Status</h2>
          <Link to="/track" className="text-xs font-semibold text-[#C2410C]">Track anytime →</Link>
        </div>
        <OrderTimeline status={order.status} />
      </div>

      <div className="mt-6 rounded-2xl bg-white border border-[#E7E0D6] overflow-hidden">
        <div className="px-6 py-4 border-b border-[#E7E0D6] flex items-center gap-2">
          <Package size={18} className="text-[#C2410C]" />
          <h2 className="font-display text-lg font-bold text-[#1F130B]">Order Details</h2>
        </div>
        <div className="p-6 space-y-4">
          {order.items.map((it, i) => (
            <div key={i} className="flex gap-3">
              <img src={it.image} alt={it.name} className="h-14 w-14 rounded-lg object-cover border border-[#E7E0D6]" />
              <div className="flex-1">
                <p className="text-sm font-medium text-[#1F130B]">{it.name}</p>
                <p className="text-xs text-[#6B5B52]">{it.weight_label} × {it.qty}</p>
              </div>
              <p className="text-sm font-semibold text-[#1F130B]">{formatINR(it.line_total)}</p>
            </div>
          ))}
          <div className="pt-4 border-t border-dashed border-[#E7E0D6] space-y-2 text-sm">
            <div className="flex justify-between text-[#6B5B52]"><span>Subtotal</span><span>{formatINR(order.subtotal)}</span></div>
            {order.discount > 0 && <div className="flex justify-between text-[#15803D]"><span>Discount {order.coupon && `(${order.coupon})`}</span><span>-{formatINR(order.discount)}</span></div>}
            <div className="flex justify-between text-[#6B5B52]"><span>Shipping</span><span>{order.shipping ? formatINR(order.shipping) : "FREE"}</span></div>
            <div className="flex justify-between text-base font-bold text-[#1F130B] pt-1"><span>Total</span><span>{formatINR(order.total)}</span></div>
          </div>
        </div>
      </div>

      <div className="mt-6 grid sm:grid-cols-2 gap-6">
        <div className="rounded-2xl bg-white border border-[#E7E0D6] p-5">
          <p className="flex items-center gap-2 font-semibold text-[#1F130B] text-sm"><MapPin size={16} className="text-[#C2410C]" /> Delivery Address</p>
          <p className="mt-2 text-sm text-[#6B5B52] leading-relaxed">
            {order.customer.name}<br />
            {order.customer.address}, {order.customer.city}<br />
            {order.customer.state} - {order.customer.pincode}<br />
            📞 {order.customer.mobile}
          </p>
        </div>
        <div className="rounded-2xl bg-white border border-[#E7E0D6] p-5">
          <p className="flex items-center gap-2 font-semibold text-[#1F130B] text-sm"><Truck size={16} className="text-[#C2410C]" /> Estimated Delivery</p>
          <p className="mt-2 text-sm text-[#6B5B52]">{order.est_delivery}</p>
          <p className="mt-2 inline-flex items-center gap-1.5 text-xs text-[#6B5B52]"><Clock size={13} /> Payment: <b className="capitalize text-[#1F130B]">{order.payment_status}</b> · {order.payment_method}</p>
        </div>
      </div>

      <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
        <Link to="/shop" className="text-center rounded-full border-2 border-[#1C120C] text-[#1C120C] font-semibold px-7 py-3 hover:bg-[#1C120C] hover:text-white transition-colors">Continue Shopping</Link>
        <a href={waLink(waMsg)} target="_blank" rel="noreferrer" data-testid="confirmation-whatsapp" className="text-center rounded-full bg-[#25D366] text-white font-semibold px-7 py-3 hover:brightness-95 inline-flex items-center justify-center gap-2">
          <MessageCircle size={18} /> Share Order on WhatsApp
        </a>
      </div>
    </div>
  );
}
